import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, User as UserIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/users/$userId")({
	component: UserDetailPage,
});

type UserDetail = {
	user: {
		id: string;
		name: string;
		email: string;
		image: string | null;
		emailVerified: boolean;
		createdAt: Date;
		updatedAt: Date;
	};
	memberships: Array<{
		id: string;
		createdAt: Date;
		organizationId: string;
		organizationName: string;
		organizationLogo: string | null;
	}>;
	projects: Array<{
		id: string;
		name: string;
		description: string | null;
		createdAt: Date;
		organizationId: string;
		organizationName: string;
	}>;
	systemRoles: {
		isSuperAdmin: boolean;
		isSupportPersonnel: boolean;
	};
};

type ClientWithUserDetail = typeof client & {
	admin: typeof client.admin & {
		users: typeof client.admin.users & {
			getUserById: (input: { userId: string }) => Promise<UserDetail>;
		};
	};
};

function UserDetailPage() {
	const { userId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "users", "detail", userId],
		queryFn: async () => {
			const result = await (
				client as ClientWithUserDetail
			).admin.users.getUserById({
				userId,
			});
			return result;
		},
		staleTime: 2 * 60 * 1000, // 2 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
	});

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-12 w-64" />
				<Skeleton className="h-96 w-full" />
			</div>
		);
	}

	if (!data) {
		return (
			<div className="py-12 text-center">
				<p className="text-muted-foreground">
					{t("admin:users.detail.not_found")}
				</p>
			</div>
		);
	}

	const { user, memberships, projects, systemRoles } = data;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Button
					variant="ghost"
					size="icon"
					onClick={() => navigate({ to: "/users" })}
				>
					<ArrowLeft className="h-4 w-4" />
				</Button>
				<div className="flex items-center gap-4">
					<Avatar className="h-12 w-12">
						<AvatarImage src={user.image || undefined} />
						<AvatarFallback>
							{user.name.substring(0, 2).toUpperCase()}
						</AvatarFallback>
					</Avatar>
					<div>
						<h1 className="font-bold text-3xl">{user.name}</h1>
						<p className="text-muted-foreground text-sm">
							{t("admin:users.detail.created_at", {
								date: new Date(user.createdAt).toLocaleDateString(),
							})}
						</p>
					</div>
				</div>
			</div>

			{/* Overview Cards */}
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-sm">
							{t("admin:users.detail.total_organizations")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="font-bold text-2xl">{memberships.length}</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-sm">
							{t("admin:users.detail.total_projects")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="font-bold text-2xl">{projects.length}</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-sm">
							{t("admin:users.detail.account_status")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						{user.emailVerified ? (
							<Badge variant="default">{t("admin:users.verified")}</Badge>
						) : (
							<Badge variant="secondary">{t("admin:users.unverified")}</Badge>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-sm">
							{t("admin:users.detail.user_id")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="font-mono text-xs">{user.id}</p>
					</CardContent>
				</Card>
			</div>

			{/* Account Information */}
			<Card>
				<CardHeader>
					<CardTitle>{t("admin:users.detail.account_information")}</CardTitle>
					<CardDescription>
						{t("admin:users.detail.account_description")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid gap-4 md:grid-cols-2">
						<div>
							<p className="text-muted-foreground text-sm">
								{t("admin:users.detail.email")}
							</p>
							<p className="font-medium">{user.email}</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t("admin:users.detail.email_verified")}
							</p>
							{user.emailVerified ? (
								<Badge variant="default">{t("admin:users.verified")}</Badge>
							) : (
								<Badge variant="secondary">{t("admin:users.unverified")}</Badge>
							)}
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t("admin:users.detail.created_date")}
							</p>
							<p className="font-medium">
								{new Date(user.createdAt).toLocaleDateString()}
							</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t("admin:users.detail.last_updated")}
							</p>
							<p className="font-medium">
								{new Date(user.updatedAt).toLocaleDateString()}
							</p>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* System Roles */}
			{(systemRoles.isSuperAdmin || systemRoles.isSupportPersonnel) && (
				<Card>
					<CardHeader>
						<CardTitle>{t("admin:users.detail.system_roles")}</CardTitle>
						<CardDescription>
							{t("admin:users.detail.system_roles_description")}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="flex gap-2">
							{systemRoles.isSuperAdmin && (
								<Badge variant="default">
									{t("admin:user_management.super_admin")}
								</Badge>
							)}
							{systemRoles.isSupportPersonnel && (
								<Badge variant="secondary">
									{t("admin:user_management.support_personnel")}
								</Badge>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Organization Memberships */}
			<Card>
				<CardHeader>
					<CardTitle>{t("admin:users.detail.organizations")}</CardTitle>
					<CardDescription>
						{t("admin:users.detail.organizations_description")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					{memberships.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("admin:users.detail.organization_name")}
									</TableHead>
									<TableHead>{t("admin:users.detail.joined_date")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{memberships.map(
									(membership: UserDetail["memberships"][number]) => (
										<TableRow key={membership.id}>
											<TableCell>
												<button
													type="button"
													onClick={() =>
														navigate({
															to: `/organizations/${membership.organizationId}`,
														})
													}
													className="flex items-center gap-3 text-left"
												>
													{membership.organizationLogo ? (
														<img
															src={membership.organizationLogo}
															alt={membership.organizationName}
															className="h-8 w-8 rounded object-cover"
														/>
													) : (
														<div className="flex h-8 w-8 items-center justify-center rounded bg-muted">
															<UserIcon className="h-4 w-4 text-muted-foreground" />
														</div>
													)}
													<span className="font-medium hover:underline">
														{membership.organizationName}
													</span>
												</button>
											</TableCell>
											<TableCell className="text-muted-foreground text-sm">
												{new Date(membership.createdAt).toLocaleDateString()}
											</TableCell>
										</TableRow>
									),
								)}
							</TableBody>
						</Table>
					) : (
						<p className="text-muted-foreground text-sm">
							{t("admin:users.detail.no_organizations")}
						</p>
					)}
				</CardContent>
			</Card>

			{/* Projects */}
			<Card>
				<CardHeader>
					<CardTitle>{t("admin:users.detail.projects")}</CardTitle>
					<CardDescription>
						{t("admin:users.detail.projects_description")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					{projects.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("admin:users.detail.project_name")}</TableHead>
									<TableHead>
										{t("admin:users.detail.project_organization")}
									</TableHead>
									<TableHead>
										{t("admin:users.detail.project_description")}
									</TableHead>
									<TableHead>
										{t("admin:users.detail.project_created")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{projects.map((project: UserDetail["projects"][number]) => (
									<TableRow key={project.id}>
										<TableCell className="font-medium">
											{project.name}
										</TableCell>
										<TableCell>
											<button
												type="button"
												onClick={() =>
													navigate({
														to: `/organizations/${project.organizationId}`,
													})
												}
												className="text-left hover:underline"
											>
												{project.organizationName}
											</button>
										</TableCell>
										<TableCell className="text-muted-foreground text-sm">
											{project.description || "-"}
										</TableCell>
										<TableCell className="text-muted-foreground text-sm">
											{new Date(project.createdAt).toLocaleDateString()}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					) : (
						<p className="text-muted-foreground text-sm">
							{t("admin:users.detail.no_projects")}
						</p>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
